/**
 * Realtime subscription hook for notifications
 * Subscribes to postgres_changes on the notifications table
 * and invalidates React Query cache when changes occur.
 * 
 * FMEA Mitigations:
 * - F1: Only subscribes when profileId is available
 * - F2: Static channel name (no Date.now()) to prevent recreation loops
 * - F3: Relies on Supabase auto-reconnect (no manual retry logic)
 * - F4: Uses refs instead of state to prevent re-render loops
 * - F5: Visibility change listener invalidates queries without reconnecting
 * - F6: Minimal dependencies in useEffect to prevent recreation
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { notificationKeys } from './useNotifications';
import { notificationPreferenceKeys } from './useNotificationPreferences';
import { devLog } from '@/utils/logger';

interface UseNotificationRealtimeOptions {
  profileId: string | undefined;
  enabled?: boolean;
}

interface ConnectionState {
  isConnected: boolean;
  lastConnectedAt: Date | null;
  reconnectCount: number;
}

export function useNotificationRealtimeSubscription({
  profileId,
  enabled = true,
}: UseNotificationRealtimeOptions) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isMountedRef = useRef(true);
  const connectionStateRef = useRef<ConnectionState>({
    isConnected: false,
    lastConnectedAt: null,
    reconnectCount: 0,
  });

  // Store queryClient in ref for stable access in callbacks
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  // Debounced invalidation to prevent query amplification from rapid
  // SUBSCRIBED + visibility events (reduces realtime.list_changes load)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const invalidateNotifications = useCallback(() => {
    if (!isMountedRef.current) return;

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      queryClientRef.current.invalidateQueries({ queryKey: notificationKeys.all });
      queryClientRef.current.invalidateQueries({ queryKey: notificationPreferenceKeys.unreadCount() });
      debounceTimerRef.current = null;
    }, 5000);
  }, []);

  // Main subscription effect - only depends on profileId and enabled
  // invalidateNotifications is accessed via ref to prevent effect re-runs
  useEffect(() => {
    isMountedRef.current = true;

    // Debug: Log effect entry to trace execution
    devLog(`[Notifications Realtime] Effect running - profileId: ${profileId ? 'present' : 'missing'}, enabled: ${enabled}`);

    // F1: Only subscribe when profileId is available and enabled
    if (!profileId || !enabled) {
      devLog('[Notifications Realtime] Skipping subscription - no profileId or disabled');
      return;
    }

    // Cleanup existing channel before creating new one
    if (channelRef.current) {
      devLog('[Notifications Realtime] Cleaning up existing channel before recreating');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // F2: Static channel name (no Date.now() to prevent loops)
    const channelName = `notifications-${profileId}`;
    devLog(`[Notifications Realtime] Creating subscription: ${channelName}`);

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${profileId}`,
        },
        (payload) => {
          if (!isMountedRef.current) return;
          
          devLog('[Notifications Realtime] Received change:', payload.eventType);
          invalidateNotifications();
        }
      )
      .subscribe((status, err) => {
        if (!isMountedRef.current) return;

        devLog(`[Notifications Realtime] Status: ${status}`);
        
        if (status === 'SUBSCRIBED') {
          const wasDisconnected = !connectionStateRef.current.isConnected;
          connectionStateRef.current.isConnected = true;
          connectionStateRef.current.lastConnectedAt = new Date();
          connectionStateRef.current.reconnectCount++;
          
          // F5 FIX: Always invalidate on SUBSCRIBED to catch any missed events
          // This ensures fresh data on initial connection AND reconnects
          devLog(`[Notifications Realtime] Connected (count: ${connectionStateRef.current.reconnectCount}), refreshing notifications`);
          invalidateNotifications();
        }
        
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          connectionStateRef.current.isConnected = false;
          console.warn('[Notifications Realtime] Connection closed/error:', err);
          // F3: Supabase client handles auto-reconnect internally - no manual retry needed
        }
      });

    channelRef.current = channel;

    // F5: Visibility change listener - invalidate but don't recreate
    const handleVisibilityChange = () => {
      if (!isMountedRef.current) return;
      
      if (document.visibilityState === 'visible') {
        devLog('[Notifications Realtime] Tab visible, refreshing data');
        invalidateNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Clear pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [profileId, enabled]); // Removed invalidateNotifications - accessed via closure, stable due to useCallback

  return {
    isConnected: connectionStateRef.current.isConnected,
  };
}
